import { zodResolver } from "@hookform/resolvers/zod";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	Accordion,
	AccordionItem,
	AccordionPanel,
	AccordionTrigger,
} from "@/components/Accordion";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import { orpc } from "@/orpc/client";
import {
	type CreateReferralLinkFormValues,
	type CreateReferralLinkOutput,
	createReferralLinkSchema,
	referralLinkDefaultsSchema,
} from "../schema";
import { BaseSection } from "./components/BaseSection";
import { FinancesSection } from "./components/FinancesSection";

export function EditTab() {
	const { appId, linkId } = useParams({
		from: "/_app/apps/$appId/links/$linkId/",
	});
	const queryClient = useQueryClient();

	// Fetch data
	const { data: referralLink } = useSuspenseQuery(
		orpc.referralLinks.detail.queryOptions({
			input: { id: linkId },
		}),
	);

	const { data: partners } = useSuspenseQuery(
		orpc.partners.list.queryOptions({
			input: { appId },
		}),
	);

	const methods = useForm<
		CreateReferralLinkFormValues,
		unknown,
		CreateReferralLinkOutput
	>({
		resolver: zodResolver(createReferralLinkSchema),
		defaultValues: referralLinkDefaultsSchema.parse(referralLink),
	});

	const { handleSubmit } = methods;

	const updateMutation = useMutation(
		orpc.referralLinks.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.referralLinks.list.key(),
				});
				queryClient.invalidateQueries({
					queryKey: orpc.referralLinks.detail.key(),
				});
				toast.success("Referral link updated");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const onSubmit = async (data: CreateReferralLinkOutput) => {
		let expiresAt: Date | null = null;

		if (data.expiresAt) {
			expiresAt = new Date(data.expiresAt);
		}

		const { payoutRateBps, ...rest } = data;
		const actualPayoutRateBps = Math.round(Number(payoutRateBps) * 100);

		updateMutation.mutate({
			id: linkId,
			...rest,
			payoutRateBps: actualPayoutRateBps,
			maxRedemptions: data.maxRedemptions ?? null,
			payoutDurationDays: data.payoutDurationDays ?? null,
			expiresAt,
		});
	};

	return (
		<FormProvider {...methods}>
			{referralLink.partnerAgreedAt ? (
				<AlertBar variant="success" title="Partner Approved">
					Partner approved on{" "}
					{new Date(referralLink.partnerAgreedAt).toLocaleDateString()}.
				</AlertBar>
			) : (
				<AlertBar variant="warning" title="Pending Partner Approval">
					This link is awaiting partner approval. It cannot be used for
					attributions until the partner approves the terms.
				</AlertBar>
			)}
			<form
				onSubmit={handleSubmit(onSubmit, console.log)}
				className="space-y-8"
			>
				<Accordion
					multiple
					defaultValue={["base-details", "finances"]}
					className="space-y-4"
				>
					<AccordionItem value="base-details">
						<AccordionTrigger>Base Details</AccordionTrigger>
						<AccordionPanel>
							<BaseSection partners={partners} />
						</AccordionPanel>
					</AccordionItem>

					<AccordionItem value="finances">
						<AccordionTrigger>Finances</AccordionTrigger>
						<AccordionPanel>
							<FinancesSection disabled />
						</AccordionPanel>
					</AccordionItem>
				</Accordion>

				<div className="flex justify-end pt-4">
					<Button type="submit" disabled={updateMutation.isPending}>
						{updateMutation.isPending ? "Saving..." : "Save Changes"}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
}
